import { z } from "zod";
import {
  CLAIM_STATUSES, DAMAGE_TYPES, GB_STATUSES, GLASS_POSITIONS, JOB_STATUSES, JOB_TYPES, MAX_PHOTOS_PER_JOB, PHOTO_KINDS,
  PRIORITIES, SYNC_STATES, VEHICLE_LOCATIONS,
} from "./enums.js";
import { validateVin } from "./vin.js";
import { stateOfZipPrefix, US_STATE_NAMES } from "./zip-prefixes.js";
import { SERVED_ZIPS } from "./served-zips.js";
import { stateOfZip } from "./zips.js";

/**
 * Any US format in ("+1 (954) 864-5741", "8633462538", "239.465.8710"), 10 digits out.
 * Area code and exchange follow NANP: neither starts with 0 or 1, and 555-01xx is fiction.
 * An extension ("x12", "ext. 12") is refused with its own message: AGW texts this number.
 */
export const usPhone = z
  .string()
  .superRefine((s, ctx) => {
    const t = s.trim();
    if (/\d\s*(?:extension|ext\.?|x|#|,|;)\s*\d{1,6}$/i.test(t)) {
      ctx.addIssue({ code: "custom", message: "Leave out the extension. Use a mobile number we can text." });
    } else if (t.startsWith("+") && !t.replace(/\D/g, "").startsWith("1")) {
      ctx.addIssue({ code: "custom", message: "Use a US phone number" });
    }
  })
  .transform((s) => s.replace(/\D/g, "").replace(/^1(?=\d{10}$)/, ""))
  .pipe(
    z
      .string()
      .regex(/^\d{10}$/, "Enter a 10-digit US phone number")
      .regex(/^[2-9]\d{2}[2-9]\d{6}$/, "That is not a valid US phone number")
      .refine((p) => !/^\d{3}55501\d{2}$/.test(p), "That is not a valid US phone number")
      .refine((p) => !/^(\d)\1{9}$/.test(p), "That is not a valid US phone number"),
  );

/** Countries the AGW team works from. US numbers keep the 10-digit form; others store as +<code><number>. */
export const PHONE_COUNTRIES = [
  { code: "1", label: "US +1", digits: /^\d{10}$/ },
  { code: "91", label: "India +91", digits: /^[6-9]\d{9}$/ },
  { code: "63", label: "Philippines +63", digits: /^9\d{9}$/ },
] as const;

/**
 * A team member's mobile. "+91 88011 43466" -> "+918801143466", "+63 917 123 4567" -> "+639171234567".
 * No plus sign, or +1, means a US number, checked like usPhone.
 */
export const staffPhone = z.string().transform((s, ctx) => {
  const t = s.trim();
  const digits = t.replace(/\D/g, "");
  if (!t.startsWith("+") || digits.startsWith("1")) {
    const us = usPhone.safeParse(t);
    if (us.success) return us.data;
    ctx.addIssue({ code: "custom", message: us.error.issues[0]?.message ?? "That is not a valid US phone number" });
    return z.NEVER;
  }
  const country = PHONE_COUNTRIES.find((c) => c.code !== "1" && digits.startsWith(c.code));
  const local = country ? digits.slice(country.code.length).replace(/^0/, "") : "";
  if (!country || !country.digits.test(local)) {
    ctx.addIssue({ code: "custom", message: "Enter a US, India (+91) or Philippines (+63) mobile number" });
    return z.NEVER;
  }
  return `+${country.code}${local}`;
});

/** Splits a stored mobile into its country code and local number, for the form. */
export const splitStaffPhone = (phone?: string): { code: string; local: string } => {
  const c = phone?.startsWith("+") ? PHONE_COUNTRIES.find((x) => x.code !== "1" && phone.startsWith(`+${x.code}`)) : undefined;
  return c ? { code: c.code, local: phone!.slice(c.code.length + 1) } : { code: "1", local: phone ?? "" };
};

/** (954) 864-5741 */
export const formatPhone = (digits?: string) => {
  if (!digits) return "";
  if (/^\d{10}$/.test(digits)) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  const { code, local } = splitStaffPhone(digits);
  return code === "1" ? digits : `+${code} ${local}`;
};

/**
 * Trim, collapse spaces, letters required, no digits. Keeps "Jairy Duarte, Guadalupe Castellano",
 * "O'Brien" and "O’Brien" (the phone keyboard's curly apostrophe), "Jean-Luc", "José Ñúñez" as typed.
 */
export const personName = z
  .string()
  .transform((s) => s.replace(/\s+/g, " ").trim())
  .pipe(
    z
      .string()
      .min(2, "Enter the customer's full name")
      .max(100)
      .regex(/^[\p{L}][\p{L}\p{M}'’ .,&()-]*$/u, "Use letters only in the name")
      .refine((n) => n.replace(/[^\p{L}]/gu, "").length >= 2, "Enter the customer's full name"),
  );

/** GlassBiller job numbers are digits. Strips the "WO ", "WO:" and "#" the sheet carries. */
export const gbJobNumber = z
  .string()
  .transform((s) => s.trim().replace(/^(wo|w\/o|#)[\s:#.]*/i, "").replace(/\s+/g, ""))
  .pipe(z.string().regex(/^\d{5,10}$/, "GlassBiller job numbers are 5 to 10 digits"));

export const vinSchema = z.string().transform((raw, ctx) => {
  const result = validateVin(raw);
  if (!result.ok) {
    ctx.addIssue({ code: "custom", message: result.message });
    return z.NEVER;
  }
  return result.vin;
});

export const vehicleSchema = z.object({
  vin: vinSchema,
  year: z.number().int().min(1981).max(2100).optional(),
  make: z.string().max(40).optional(),
  model: z.string().max(60).optional(),
  trim: z.string().max(60).optional(),
  color: z.string().max(30).optional(),
  stockNumber: z.string().max(30).optional(),
  roNumber: z.string().max(30).optional(),
  mileage: z.number().int().nonnegative().max(2_000_000).optional(),
});

export const usZip = z.string().trim().regex(/^\d{5}$/, "Enter a 5-digit ZIP");

/**
 * Why a 5-digit ZIP can't take a claim, naming its state; undefined when AGW serves it. Once the
 * office marks the ZIP sheet (served-zips.ts), only marked ZIPs pass; until then, any FL, SC or NC ZIP.
 */
export function zipProblem(zip: string, served: ReadonlySet<string> = SERVED_ZIPS): string | undefined {
  const state = stateOfZip(zip);
  if (state && (served.size === 0 || served.has(zip))) return undefined;
  if (state) return `We don't serve ZIP ${zip} yet. Call the Auto Glass World office before you send this claim.`;
  const prefixState = stateOfZipPrefix(zip);
  if (!prefixState || prefixState === "FL" || state === "SC" || state === "NC") return `We can't find ZIP ${zip}. Check it.`;
  return `ZIP ${zip} is in ${US_STATE_NAMES[prefixState] ?? prefixState}. We serve FL, SC and NC only.`;
}

const NO_LIST: ReadonlySet<string> = new Set();

/**
 * A real ZIP in Florida, South Carolina or North Carolina. Whether AGW serves it is checked against the
 * list admins keep in the portal (the claim form and submitJob), since that list lives in the database.
 */
export const servedZip = usZip.superRefine((zip, ctx) => {
  const problem = zipProblem(zip, NO_LIST);
  if (problem) ctx.addIssue({ code: "custom", message: problem });
});

const policyNumber = z
  .string()
  .transform((s) => s.replace(/\s+/g, " ").trim().toUpperCase())
  .pipe(z.string().regex(/^[A-Z0-9][A-Z0-9 ./()-]{3,39}$/, "Policy numbers are 4 to 40 letters and digits"));

const CASH_CARRIERS = new Set(["Cash (Quote)"]);

/** The customer and their insurance: the Claims sheet's customer columns. */
export const insuranceSchema = z.object({
  customerName: personName,
  customerPhone: usPhone,
  customerEmail: z.email().optional(),
  /** Where the customer is, for the ZIP-level reports and scheduling. FL, SC or NC only. */
  customerZip: servedZip,
  carrier: z.string().trim().min(2, "Pick the insurance carrier").max(80),
  /** Required unless the carrier is a cash quote. */
  policyNumber: policyNumber.optional(),
  claimNumber: z.string().max(40).optional(),
  /** "Referral" column: the carrier's or network's referral number. */
  referralNumber: z
    .string()
    .transform((s) => s.trim().toUpperCase())
    .pipe(z.string().regex(/^[A-Z0-9-]{3,40}$/, "Referral numbers are letters and digits"))
    .optional(),
  dateOfLoss: z.iso.date().optional(),
  deductible: z.number().nonnegative().max(10_000).optional(),
  insuredAddress: z.string().max(200).optional(),
})
  .superRefine((ins, ctx) => {
    if (!CASH_CARRIERS.has(ins.carrier) && !ins.policyNumber) {
      ctx.addIssue({ code: "custom", path: ["policyNumber"], message: "Enter the policy number" });
    }
    if (ins.dateOfLoss) {
      const dol = Date.parse(`${ins.dateOfLoss}T12:00:00Z`);
      if (dol > Date.now() + 86_400_000) ctx.addIssue({ code: "custom", path: ["dateOfLoss"], message: "Date of loss can't be in the future" });
      if (dol < Date.now() - 2 * 365 * 86_400_000) ctx.addIssue({ code: "custom", path: ["dateOfLoss"], message: "Date of loss is over 2 years ago. Check the date." });
    }
  });

export const photoRefSchema = z.object({
  /** Server-issued (pho_<ulid>). No slashes or dots, so it can never point outside the uploader's staging folder. */
  photoId: z.string().regex(/^[A-Za-z0-9_-]{1,64}$/, "Unknown photo"),
  kind: z.enum(PHOTO_KINDS),
});

/** What a dealer submits. The API re-validates it against the store's form config. */
export const submitJobSchema = z
  .object({
    clientRequestId: z.uuid(), // idempotency key generated on the device; safe offline retries
    storeId: z.string().min(1),
    vehicle: vehicleSchema,
    // A rep's claim is a windshield insurance job at the dealership unless they say otherwise.
    location: z
      .object({
        kind: z.enum(VEHICLE_LOCATIONS),
        note: z.string().max(200).optional(),
        address: z.string().max(200).optional(),
      })
      .default({ kind: "service_lane" }),
    glass: z
      .object({
        // The form starts on windshield; the rep can pick other glass. Older clients send nothing: windshield.
        positions: z.array(z.enum(GLASS_POSITIONS)).min(1, "Pick the glass").default(["windshield"]),
        damage: z.enum(DAMAGE_TYPES).optional(),
        adasCalibration: z.boolean().default(false),
      })
      .default({ positions: ["windshield"], adasCalibration: false }),
    jobType: z.enum(JOB_TYPES).default("insurance"),
    poNumber: z.string().max(40).optional(),
    insurance: insuranceSchema.optional(),
    timing: z
      .object({
        neededBy: z.iso.date().optional(),
        priority: z.enum(PRIORITIES).default("normal"),
        customerWaiting: z.boolean().default(false),
      })
      .default({ priority: "normal", customerWaiting: false }),
    photos: z.array(photoRefSchema).max(MAX_PHOTOS_PER_JOB).default([]),
    notes: z.string().max(2000).optional(),
    customerContactAttested: z.boolean().default(false),
    acknowledgeDuplicate: z.boolean().default(false),
  })
  .superRefine((job, ctx) => {
    if (job.jobType === "insurance" && !job.insurance) {
      ctx.addIssue({ code: "custom", path: ["insurance"], message: "Insurance details are required for insurance jobs" });
    }
    if (job.jobType === "insurance" && !job.customerContactAttested) {
      ctx.addIssue({
        code: "custom",
        path: ["customerContactAttested"],
        message: "Confirm the customer agreed to be contacted",
      });
    }
    if (job.location.kind === "customer_address" && !job.location.address) {
      ctx.addIssue({ code: "custom", path: ["location", "address"], message: "Address is required" });
    }
  });

export type SubmitJobInput = z.input<typeof submitJobSchema>;
export type SubmitJob = z.output<typeof submitJobSchema>;

export const syncStatusSchema = z.object({
  state: z.enum(SYNC_STATES),
  externalId: z.string().optional(),
  attempts: z.number().int().nonnegative(),
  lastError: z.string().optional(),
  updatedAt: z.iso.datetime(),
});

/** What the API returns for a job. Customer PII is included only for roles that may see it. */
export const jobViewSchema = z.object({
  jobId: z.string(),
  jobNumber: z.string(),
  status: z.enum(JOB_STATUSES),
  storeId: z.string(),
  submittedBy: z.object({ userId: z.string(), name: z.string() }),
  submittedAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  vehicle: vehicleSchema.extend({ vin: z.string() }),
  glass: z.object({
    positions: z.array(z.enum(GLASS_POSITIONS)),
    damage: z.enum(DAMAGE_TYPES).optional(),
    adasCalibration: z.boolean(),
  }),
  jobType: z.enum(JOB_TYPES),
  priority: z.enum(PRIORITIES),
  glassBillerJobNumber: z.string().optional(),
  scheduledFor: z.iso.datetime().optional(),
  installedAt: z.iso.datetime().optional(),
  expectedPaymentDate: z.iso.date().optional(),
  photoCount: z.number().int(),
  sync: z.object({ glassbiller: syncStatusSchema, ghl: syncStatusSchema }).optional(),
});
export type JobView = z.infer<typeof jobViewSchema>;

/** Office side of a claim: the sheet's Filed By, Scheduled, Schedule Date, GB Status, GB Link and Notes. */
export const claimOfficeSchema = z.object({
  status: z.enum(CLAIM_STATUSES),
  statusChangedAt: z.iso.datetime(),
  filedBy: z.string().max(60).optional(),
  scheduleNote: z.string().max(120).optional(),
  gbStatus: z.enum(GB_STATUSES).optional(),
  gbLink: z.string().max(300).optional(),
  officeNotes: z.string().max(4000).optional(),
});
export type ClaimOffice = z.infer<typeof claimOfficeSchema>;

/** Admin edit, or the GHL webhook. Every field optional; only sent fields change. */
export const claimUpdateSchema = z.object({
  status: z.enum(CLAIM_STATUSES).optional(),
  filedBy: z.string().trim().max(60).optional(),
  scheduleNote: z.string().trim().max(120).optional(),
  scheduledFor: z.iso.datetime().optional(),
  installedAt: z.iso.datetime().optional(),
  glassBillerJobNumber: gbJobNumber.optional(),
  gbStatus: z.enum(GB_STATUSES).optional(),
  /** Empty clears it. Only http(s), so a GHL post or an edit can never plant a javascript: link. */
  gbLink: z.string().trim().max(300).refine((v) => v === "" || /^https?:\/\/[^\s]+$/i.test(v), "Use a full https:// link").optional(),
  officeNotes: z.string().max(4000).optional(),
});
export type ClaimUpdate = z.infer<typeof claimUpdateSchema>;

/** Customer fields on a job view. Phone and policy number are left out for dealership logins. */
export const customerViewSchema = z.object({
  name: z.string(),
  zip: z.string().optional(),
  carrier: z.string().optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  policyNumber: z.string().optional(),
  referralNumber: z.string().optional(),
  dateOfLoss: z.string().optional(),
});

/** A claim as every screen sees it: the job view plus the sheet's customer and office columns. */
export const claimViewSchema = jobViewSchema.extend({
  storeName: z.string().optional(),
  state: z.string().optional(),
  customer: customerViewSchema.optional(),
  notes: z.string().optional(),
  claim: claimOfficeSchema,
  /** Photos from the old Google Form (Drive links), on imported claims only. */
  legacyPhotos: z.array(z.object({ label: z.string(), url: z.string() })).optional(),
});
export type ClaimView = z.infer<typeof claimViewSchema>;
